import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { DataTable } from "@/components/data-table.tsx";
import { ActionItem } from "@/features/Table/Table";
import { Spinner } from "@heroui/spinner";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useTable } from "./useTable";

type TableProps<TData, TValue> = {
  label: string;
  client: string;
  table_name: string;
  columns: ColumnDef<TData, TValue>[];
  columnVisibilityInit?: VisibilityState;
  filter?: React.ReactNode;
  new_item?: React.ReactNode;
  audit_id?: number;
  download_button?: boolean;
  actions?: ActionItem<TData>[];
};

export default function Table<TData, TValue>({
  label,
  client,
  table_name,
  columns,
  columnVisibilityInit,
  filter = null,
  new_item = null,
  audit_id = undefined,
  download_button = true,
  actions = [],
}: TableProps<TData, TValue>) {
  const { t, i18n } = useTranslation(["tables"]);
  const { data, isLoading, error, currentPage, pagesize, setCurrentPage } =
    useTable<TData>({ client, table_name, audit_id });
  const isAuditTable = table_name.endsWith("/audit");
  const baseTableName = isAuditTable
    ? table_name.slice(0, -"/audit".length)
    : table_name;
  const translatedLabel = isAuditTable
    ? label
    : t(`${baseTableName}.title`, {
        ns: "tables",
        defaultValue: label,
      });
  const translatedColumns = useMemo(
    () =>
      columns.map((column) => {
        if (typeof column.header !== "string") return column;
        if (!("accessorKey" in column)) return column;
        if (typeof column.accessorKey !== "string") return column;
        return {
          ...column,
          header: t(`${baseTableName}.columns.${column.accessorKey}`, {
            ns: "tables",
            defaultValue: column.header,
          }),
        };
      }),
    [columns, t, i18n.language, baseTableName],
  );

  if (isLoading)
    return (
      <div className="grid place-items-center h-full">
        <Spinner />
      </div>
    );

  if (error) return "An error has occurred: " + error;

  return (
    <section className="py-4 px-3 font-sans font-light text-gray-500 leading-5">
      <DataTable<TData, TValue>
        client={client}
        table_name={table_name}
        label={translatedLabel}
        columns={translatedColumns}
        filter={filter}
        new_item={new_item}
        data={data?.data || []}
        rowCount={data?.count || 0}
        pageCount={Math.ceil((data?.count || 0) / Number(pagesize))}
        currentPage={Number(currentPage)}
        setCurrentPage={setCurrentPage}
        columnVisibilityInit={columnVisibilityInit}
        download_button={download_button}
        actions={actions}
      />
    </section>
  );
}
